import React, {
  ChangeEvent,
  FC,
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import AvatarEditor from 'react-avatar-editor';
import { toast } from 'react-toastify';
import { Button, Label } from 'reactstrap';
import { OptionFileInput } from '../../../shared/options';
import { noImage } from '../../../shared/orders/product';
import { RegistrationUserInfo } from '../../../shared/registration/model';
import { maxProfilePictureWidth } from '../../../shared/user/update';
import { MaterialIcon } from '../../components';
import { useTranslation } from '../../translations';
import { UserAvatar } from '../../utils/UserAvatar';

import './profilePictureEditor.scss';

interface ProfilePictureEditorProps {
  readonly inputUser: RegistrationUserInfo;
  readonly title?: string;
  readonly ignoreGravatar: boolean;
  readonly onChange: (file: OptionFileInput | null | undefined) => void;
}

export const ProfilePictureEditor: FC<ProfilePictureEditorProps> = ({
  inputUser,
  title,
  ignoreGravatar,
  onChange,
}) => {
  const { ts } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const avatarEditorRef = useRef<AvatarEditor>(null);
  const [newFile, setNewFile] = useState<File | null>(null);
  const [remove, setRemove] = useState(false);
  const canRemove =
    !!inputUser.profilePictureUrl && !inputUser.profilePictureUrl.includes('gravatar');

  const onChangeFile = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const eventFiles = e.target.files;
        setNewFile(eventFiles.item(0));
      }
    },
    [setNewFile],
  );

  const onClickUpload = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  }, [inputRef]);

  const cancel = useCallback(() => {
    setNewFile(null);
    onChange(undefined);
    setRemove(false);
  }, [onChange, setNewFile]);

  const onLoadFailure = useCallback(() => {
    toast.error(ts('not_a_valid_image'));
    cancel();
  }, [cancel, ts]);

  const onImageChange = useCallback(() => {
    const canvas = avatarEditorRef.current?.getImageScaledToCanvas();

    if (!canvas) {
      return;
    }

    const result = canvas.toDataURL();
    const data = result.slice(result.indexOf('base64') + 7);

    setRemove(false);
    onChange({
      contentType: 'image/png',
      fileName: 'avatar.png',
      size: result.length,
      data,
    });
  }, [onChange]);

  const onDelete = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      onChange(null);
      setRemove(true);
    },
    [onChange],
  );

  useEffect(() => {
    cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputUser.profilePictureUrl]);

  return (
    <>
      {title && <Label for="profilePictureInput">{title}</Label>}
      <div className="file-upload">
        <input
          accept="image/*"
          className="file-upload-input"
          id="profilePictureInput"
          onChange={onChangeFile}
          ref={inputRef}
          type="file"
        />
      </div>
      {newFile ? (
        <div>
          <AvatarEditor
            border={0}
            borderRadius={maxProfilePictureWidth / 2}
            height={maxProfilePictureWidth}
            image={newFile}
            onImageChange={onImageChange}
            onImageReady={onImageChange}
            onLoadFailure={onLoadFailure}
            ref={avatarEditorRef}
            width={maxProfilePictureWidth}
          />
          <br />
          <Button onClick={cancel}>{ts('common.cancel')}</Button>
        </div>
      ) : (
        <DefaultProfilePicture
          canRemove={canRemove}
          ignoreGravatar={ignoreGravatar}
          onClickUpload={onClickUpload}
          onDelete={onDelete}
          remove={remove}
          user={inputUser}
        />
      )}
    </>
  );
};

function getProfilePic(profilePic: string, ignoreGravatar: boolean) {
  if (profilePic.includes('gravatar') && ignoreGravatar) {
    return noImage;
  }

  return profilePic;
}

interface DefaultProfilePictureProps {
  readonly canRemove: boolean;
  readonly remove: boolean;
  readonly user: RegistrationUserInfo;
  readonly ignoreGravatar: boolean;
  readonly onDelete: MouseEventHandler<HTMLSpanElement>;
  readonly onClickUpload: () => void;
}

const DefaultProfilePicture: FC<DefaultProfilePictureProps> = ({
  canRemove,
  remove,
  user,
  ignoreGravatar,
  onDelete,
  onClickUpload,
}) => {
  const profilePic = getProfilePic(user.profilePictureUrl || '', ignoreGravatar);
  const { ts } = useTranslation();

  return (
    <div
      id="uploadProfilePic"
      onClick={onClickUpload}
      style={{ position: 'relative', cursor: 'pointer' }}
    >
      <div className="overlay align-content-center" style={{ fontWeight: 'bold' }}>
        <div>
          {ts('common.click')}
          <br />
          {ts('common.to')}
          <br />
          {ts('common.update')}
        </div>
        {canRemove && !remove && (
          <div className="profilePictureDeleteButton">
            <MaterialIcon name="delete" onClick={onDelete} />
          </div>
        )}
      </div>
      {remove ? (
        <UserAvatar size="large" url={noImage} />
      ) : (
        <UserAvatar size="large" url={profilePic} />
      )}
    </div>
  );
};
